/**
 * Projection for `RemoteSupportScene` — the demo's `renderRemote()` (line
 * 3076).
 *
 * Pure: no React, no fetching, no engine mutation, no `Date`.
 *
 * ## The duplicate session id
 *
 * `RemoteSession.session_id` is **not unique**: 36 rows carry 35 distinct ids
 * (`XS-2016` appears twice). The demo indexes on the id and so silently shows
 * one of the two whenever that id is selected. The port keeps the id as the URL
 * key — it is what `shared/oem-routes.ts` addresses and what a citation names —
 * but adds a composite {@link SessionRow.key} for React and for the selected
 * row, so the two rows are never confused with each other.
 *
 * Transformation layer (layer 3 of the 4-layer architecture).
 *
 * @see src/versions/v1/modules/service/RemoteSupportScene.tsx
 * @see docs/logic/workflows/remote-support.md
 * @see docs/foundations/4-layer-architecture.md
 */

import { BY_ID } from '../../data/core';
import type { LakeEntry } from '../../schema/document';
import { MSHORT } from '../../shared/oem-engine';
import type { KnowledgeArticle, RemoteSession } from '../../shared/useOemData';

/** Demo line 3079. The notional cost of a kilometre, including time. */
export const AUD_PER_KM = 1.9;

/** What the surface needs loaded. */
export interface RemoteSupportCtx {
  readonly sessions: readonly RemoteSession[];
  readonly kb: readonly KnowledgeArticle[];
}

/** Demo line 3081. One row of the session table. */
export interface SessionRow {
  readonly session: RemoteSession;
  /**
   * Stable key. `session_id` alone is not unique — see the file header — so the
   * start timestamp is appended, and the pair is unique across the 36 rows.
   */
  readonly key: string;
  /** Mine short code beside the machine id. */
  readonly mineShort: string;
  /** `'09-08 14:20'` — the demo's slice of the ISO timestamp. */
  readonly startedLabel: string;
  /** Topic, truncated the demo's way at 84 characters. */
  readonly topicShort: string;
  /** `'Tablet'` — the device without its parenthetical. */
  readonly deviceShort: string;
  /** `'Resolved remotely'` — the outcome before its em dash. */
  readonly outcomeShort: string;
  /** Tone for the outcome chip. */
  readonly outcomeTone: 'good' | 'warn' | 'bad';
}

/** Demo line 3081. The outcome chip's tone. */
function outcomeTone(outcome: string): SessionRow['outcomeTone'] {
  if (/Resolved/.test(outcome)) return 'good';
  if (/Guided/.test(outcome)) return 'warn';
  return 'bad';
}

/** Demo line 3076. Every session, newest first. */
export function sessionRows(ctx: RemoteSupportCtx): SessionRow[] {
  return ctx.sessions
    .slice()
    .sort((a, b) => b.started.localeCompare(a.started))
    .map((session) => ({
      session,
      key: `${session.session_id}|${session.started}`,
      mineShort: BY_ID[session.machine] ? MSHORT[BY_ID[session.machine].mine] : '',
      startedLabel: session.started.slice(5, 16).replace('T', ' '),
      topicShort:
        session.topic.length > 84 ? `${session.topic.slice(0, 82)}…` : session.topic,
      deviceShort: session.device.split(' (')[0],
      outcomeShort: session.outcome.split(' — ')[0],
      outcomeTone: outcomeTone(session.outcome),
    }));
}

/** Demo line 3077. One expert's tally, for the sessions tile's detail line. */
export interface ExpertTally {
  readonly expert: string;
  /** First name only — the demo's detail line is tight. */
  readonly short: string;
  readonly sessions: number;
  readonly firstTimeFix: number;
}

/** Demo line 3078. Sessions by expert, in first-seen order. */
export function expertTallies(rows: readonly SessionRow[]): ExpertTally[] {
  const order: string[] = [];
  const byExpert = new Map<string, { sessions: number; firstTimeFix: number }>();
  rows.forEach(({ session }) => {
    if (!byExpert.has(session.expert)) {
      byExpert.set(session.expert, { sessions: 0, firstTimeFix: 0 });
      order.push(session.expert);
    }
    const tally = byExpert.get(session.expert)!;
    tally.sessions += 1;
    if (session.first_time_fix) tally.firstTimeFix += 1;
  });
  return order.map((expert) => ({
    expert,
    short: expert.split(' ')[0],
    ...byExpert.get(expert)!,
  }));
}

/** Demo line 3079. The four headline measures. */
export interface RemoteSummary {
  readonly sessions: number;
  readonly thisMonth: number;
  /** First-time-fix share, 0–100, rounded. */
  readonly firstTimeFixPct: number;
  readonly escalated: number;
  readonly kmAvoided: number;
  /** `kmAvoided` in thousands, one decimal. */
  readonly thousandKm: string;
  /** Road cost avoided, in thousands of AUD, rounded. */
  readonly audThousand: number;
  /** Hours of session time, rounded. */
  readonly hoursOnCamera: number;
  readonly articles: number;
}

/** Demo line 3077. The measures behind the header chip and the four tiles. */
export function remoteSummary(ctx: RemoteSupportCtx): RemoteSummary {
  const s = ctx.sessions;
  const km = s.reduce((a, x) => a + x.travel_avoided_km, 0);
  return {
    sessions: s.length,
    thisMonth: s.filter((x) => x.started >= '2026-09-01').length,
    firstTimeFixPct: s.length
      ? Math.round((s.filter((x) => x.first_time_fix).length / s.length) * 100)
      : 0,
    escalated: s.filter((x) => /Escalated/.test(x.outcome)).length,
    kmAvoided: km,
    thousandKm: (km / 1000).toFixed(1),
    audThousand: Math.round((km * AUD_PER_KM) / 1000),
    hoursOnCamera: Math.round(s.reduce((a, x) => a + x.duration_min, 0) / 60),
    articles: ctx.kb.length,
  };
}

/** Demo line 3077. The lake's session report for a session, when there is one. */
export const reportFor = (
  entries: readonly LakeEntry[],
  session: Pick<RemoteSession, 'session_id'>,
): LakeEntry | undefined => entries.find((e) => e.file.startsWith(`${session.session_id}_`));

/** Demo line 3077. The knowledge article a session produced, when it produced one. */
export const articleFor = (
  ctx: RemoteSupportCtx,
  session: Pick<RemoteSession, 'knowledge_article'>,
): KnowledgeArticle | undefined =>
  session.knowledge_article
    ? ctx.kb.find((k) => k.id === session.knowledge_article)
    : undefined;

/**
 * Demo line 3082. The lake file behind a knowledge article — its `open` link
 * reads the article out of the lake, not out of `service.kb`, so the drawer
 * shows the path and connector the file actually came through.
 */
export const articleEntryFor = (
  entries: readonly LakeEntry[],
  article: Pick<KnowledgeArticle, 'file'>,
): LakeEntry | undefined => entries.find((e) => e.file === article.file);

/**
 * Demo line 3087. The excerpt the demo shows from an article: the "What to
 * check" section, up to the "Where" heading.
 *
 * Falls back to the paragraph after the title when the article is not written
 * to that shape, rather than rendering an empty panel.
 */
export function articleExcerpt(article: KnowledgeArticle): string {
  const section = article.text.split('## What to check')[1]?.split('## Where')[0]?.trim();
  if (section) return section;
  return article.text
    .split('\n')
    .filter((line) => line.trim() && !line.startsWith('#'))
    .slice(0, 4)
    .join('\n')
    .trim();
}

/** One article in the knowledge list, with the session that produced it. */
export interface ArticleRow {
  readonly article: KnowledgeArticle;
  /** Session ids citing the article, `' · '` joined; `''` where none does. */
  readonly sources: string;
}

/** The knowledge base, with each article's source sessions resolved. */
export function articleRows(ctx: RemoteSupportCtx): ArticleRow[] {
  return ctx.kb.map((article) => ({
    article,
    sources: ctx.sessions
      .filter((s) => s.knowledge_article === article.id)
      .map((s) => s.session_id)
      .join(' · '),
  }));
}
